import { create } from 'zustand';
import { FulfillmentDashboardStore } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.store.types';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import { ThresholdLevel } from 'pages/ReportsPage/components/AnalyticsDashboards/chartColors';
import {
  PositionCalloutRate,
  TimeframeFulfillment,
  PositionFulfillment,
  UnfulfilledShiftDetail,
  UnfulfilledShiftDetailResponse,
  TimeframeFulfillmentResponseItem,
  PositionCalloutRateResponseItem,
  PositionCalloutRateResponse,
  LastMinuteCancellation,
  LastMinuteCancellationRow,
  LastMinuteCancellationsResponse,
  CancellationSource,
} from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.store.types';
import {
  toTimeframeLabel,
  mapPeakCalloutHours,
  mapResolutionSpeed,
  TIMEFRAMES,
  toNumber,
  PeakCalloutHoursResponseItem,
  CalloutResolutionPerTimeframeResponse,
} from 'pages/ReportsPage/components/AnalyticsDashboards/calloutMappers';
import {
  TotalShiftsResponse,
  FulfillmentByPositionResponseItem,
  FulfillmentByPositionResponse,
} from 'pages/ReportsPage/components/AnalyticsDashboards/OverviewDashboard/OverviewDashboard.types';
import { LAST_MINUTE_CANCELLATIONS_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/lastMinuteCancellations.stub';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { fetchDashboardPage } from 'pages/ReportsPage/components/AnalyticsDashboards/fetchDashboardPage';
import { unwrapDashboardRoot } from 'pages/ReportsPage/components/AnalyticsDashboards/unwrapDashboardRoot';
import {
  hasDataLockRejection,
  isDataLockError,
} from 'pages/ReportsPage/store/reportsDataHealth.store';
import { FULFILLMENT_DATA_LOCKED_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/FulfillmentDashboard/store/fulfillmentDashboard.stub';
import {
  DASHBOARDS_SHIFTS_TOTAL_ASSIGNED,
  DASHBOARDS_SHIFTS_UNFULFILLED_SHIFT_DETAIL,
  DASHBOARDS_SHIFTS_TOTAL_CANCELLED,
  DASHBOARDS_SHIFTS_FULFILLMENT_BY_TIMEFRAME,
  DASHBOARDS_SHIFTS_CALLOUT_RATE_BY_POSITION,
  DASHBOARDS_SHIFTS_FULFILLMENT_BY_POSITION,
  DASHBOARDS_SHIFTS_TOTAL_CALLOUT,
  DASHBOARDS_SHIFTS_FULFILLMENT_PERCENTAGE,
  DASHBOARDS_SHIFTS_PEAK_CALLOUT_HOURS,
  DASHBOARDS_SHIFTS_CALLOUT_RESOLUTION_PER_TIMEFRAME,
  DASHBOARDS_SHIFTS_LAST_MINUTE_CANCELLATIONS,
} from 'constants/endpoints';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

function buildFilterQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    time_range: {
      start_time: dateRange[0],
      end_time: dateRange[1],
    },
  };

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.employment_statuses = employmentTypes;
  }

  return { filters };
}

const INITIAL_STATE = {
  totalAssignedShifts: 0,
  totalAssignedShiftsPeriodDays: null as number | null,
  totalAssignedShiftsChangePrct: null as number | null,
  totalAssignedShiftsError: false,
  totalCalloutShifts: 0,
  totalCalloutShiftsPeriodDays: null as number | null,
  totalCalloutShiftsChangePrct: null as number | null,
  totalCalloutShiftsError: false,
  fulfillmentRate: 0,
  fulfillmentRatePeriodDays: null as number | null,
  fulfillmentRateChangePrct: null as number | null,
  fulfillmentRateError: false,
  cancelledShifts: 0,
  cancelledShiftsPeriodDays: null as number | null,
  cancelledShiftsChangePrct: null as number | null,
  cancelledShiftsError: false,
  lastMinuteCancellationDetails: null,
  lastMinuteCancellationTotalCount: 0,
  lastMinuteCancellationPage: 1,
  lastMinuteCancellationTotalPages: 1,
  lastMinuteCancellationLoading: false,
  lastMinuteCancellationError: false,
  calloutRateByPosition: null,
  calloutRateByPositionPage: 1,
  calloutRateByPositionTotalPages: 1,
  calloutRateByPositionLoadingMore: false,
  fulfillmentByTimeframe: null,
  fulfillmentByPosition: null,
  fulfillmentByPositionPage: 1,
  fulfillmentByPositionTotalPages: 1,
  fulfillmentByPositionLoadingMore: false,
  peakCalloutHours: null,
  resolutionSpeed: null,
  unfulfilledShiftDetails: null,
  unfulfilledShiftDetailPage: 1,
  unfulfilledShiftDetailTotalPages: 1,
  unfulfilledShiftDetailLoading: false,
  unfulfilledShiftDetailError: false,
  isLoading: false,
  error: null,
};

function getThresholdLevel(pct: number): ThresholdLevel {
  if (pct >= 85) return ThresholdLevel.ON_TARGET;
  if (pct >= 75) return ThresholdLevel.MONITOR;
  if (pct >= 65) return ThresholdLevel.ATTENTION;
  return ThresholdLevel.CRITICAL;
}

function mapCalloutRateByPosition(
  response: PositionCalloutRateResponseItem[],
): PositionCalloutRate[] {
  return response.map((item) => ({
    position: item.position_name,
    assignedShifts: item.assigned_count,
    calloutShifts: item.callout_count,
  }));
}

function mapFulfillmentByPosition(
  response: FulfillmentByPositionResponseItem[],
): PositionFulfillment[] {
  return response.map((item) => {
    const fulfillmentPercentage = toNumber(item.fulfillment_pct);
    return {
      position: item.position_name,
      fulfillmentPercentage,
      totalShifts: item.fulfilled_count,
      thresholdLevel: getThresholdLevel(fulfillmentPercentage),
      avgCalloutFulfillmentLagHours:
        item.average_callout_fulfillment_lag_hours == null
          ? null
          : toNumber(item.average_callout_fulfillment_lag_hours),
    };
  });
}

function mapFulfillmentByTimeframe(
  response: TimeframeFulfillmentResponseItem[],
): TimeframeFulfillment[] {
  const byTimeframe = new Map(
    response.map((item) => [toTimeframeLabel(item.shift_timeframe), item]),
  );
  return TIMEFRAMES.map((timeframe) => {
    const item = byTimeframe.get(timeframe);
    const fulfillmentPercentage = toNumber(item?.fulfillment_pct);
    return {
      timeframe,
      fulfillmentPercentage,
      totalShifts: item?.fulfilled_count ?? 0,
      thresholdLevel: getThresholdLevel(fulfillmentPercentage),
    };
  });
}

function formatLeadTime(hoursDecimal: number): string {
  const value = Number(hoursDecimal);
  if (!Number.isFinite(value)) return 'N/A';
  const totalMinutes = Math.round(value * 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
  if (hours > 0) return `${hours}h`;
  return `${minutes}m`;
}

function mapUnfulfilledShiftDetailResponse(
  rows: UnfulfilledShiftDetailResponse['unfulfilled_shift_details'],
): UnfulfilledShiftDetail[] {
  return rows.map((row) => ({
    date: row.date,
    shiftId: String(row.shift_number),
    location: row.community,
    position: row.position,
    timeframe: toTimeframeLabel(row.shift_timeframe),
    totalEligible: row.total_eligible,
    available: row.total_available,
    otEligible: row.overtime_eligible ?? 0,
    otAllowed: row.allow_overtime ? 'Yes' : 'No',
    noResponseReason: row.top_skip_reason || 'N/A',
    skipReasonBreakdown: row.skip_reason_breakdown ?? {},
    leadTime: formatLeadTime(row.lead_time_hours),
  }));
}

const CANCELLED_BY_LABELS: Record<string, CancellationSource> = {
  scheduler: 'Scheduler',
  team_member: 'Team Member',
  agency: 'Agency',
  bookjane_support: 'BookJane Support',
};

function mapLastMinuteCancellations(rows: LastMinuteCancellationRow[]): LastMinuteCancellation[] {
  return rows.map((row) => ({
    date: row.date,
    shiftId: `Shift #${row.shift_number}`,
    location: row.community_name,
    position: row.position_name,
    timeframe: row.timeframe ? toTimeframeLabel(row.timeframe) : '—',
    cancelledBy: (row.cancelled_by_type && CANCELLED_BY_LABELS[row.cancelled_by_type]) || '—',
    teamMember: row.team_member_name ?? '—',
    hoursBeforeStart: `${toNumber(row.hours_before_start).toFixed(1)}h`,
  }));
}

let fetchId = 0;
let unfulfilledDetailFetchId = 0;
let lastMinuteCancellationFetchId = 0;
let calloutRateByPositionBaseQuery: Record<string, unknown> | null = null;
let fulfillmentByPositionBaseQuery: Record<string, unknown> | null = null;

export const fulfillmentDashboardStore = create<FulfillmentDashboardStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchUnfulfilledShiftDetails: (page, dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++unfulfilledDetailFetchId;
      set({ unfulfilledShiftDetailLoading: true, unfulfilledShiftDetailError: false });

      const query = {
        ...buildFilterQuery(dateRange, locationIds, positions, employmentTypes),
        page,
      };

      createDashboardsRequest<UnfulfilledShiftDetailResponse>({
        url: DASHBOARDS_SHIFTS_UNFULFILLED_SHIFT_DETAIL(),
        method: 'GET',
        query,
      }).then(
        (response) => {
          if (currentFetchId !== unfulfilledDetailFetchId) return;
          const { meta, unfulfilled_shift_details } = response.data;
          set({
            unfulfilledShiftDetails: mapUnfulfilledShiftDetailResponse(unfulfilled_shift_details),
            unfulfilledShiftDetailPage: meta.current_page,
            unfulfilledShiftDetailTotalPages: meta.total_pages,
            unfulfilledShiftDetailLoading: false,
            unfulfilledShiftDetailError: false,
          });
        },
        (error) => {
          const isDataLocked = isDataLockError(error);
          if (currentFetchId !== unfulfilledDetailFetchId) return;
          if (isDataLocked) {
            set({
              unfulfilledShiftDetails: FULFILLMENT_DATA_LOCKED_STUB.unfulfilledShiftDetails,
              unfulfilledShiftDetailPage: 1,
              unfulfilledShiftDetailTotalPages: 1,
              unfulfilledShiftDetailLoading: false,
              unfulfilledShiftDetailError: false,
            });
            return;
          }
          set({
            unfulfilledShiftDetails: null,
            unfulfilledShiftDetailLoading: false,
            unfulfilledShiftDetailError: true,
          });
        },
      );
    },

    fetchLastMinuteCancellations: (page, dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++lastMinuteCancellationFetchId;
      set({ lastMinuteCancellationLoading: true, lastMinuteCancellationError: false });

      const query = {
        ...buildFilterQuery(dateRange, locationIds, positions, employmentTypes),
        page,
      };

      createDashboardsRequest<LastMinuteCancellationsResponse>({
        url: DASHBOARDS_SHIFTS_LAST_MINUTE_CANCELLATIONS(),
        method: 'GET',
        query,
      }).then(
        (response) => {
          if (currentFetchId !== lastMinuteCancellationFetchId) return;
          const { meta, last_minute_cancellations } = response.data;
          set({
            lastMinuteCancellationDetails: mapLastMinuteCancellations(last_minute_cancellations),
            lastMinuteCancellationTotalCount: meta.total_count,
            lastMinuteCancellationPage: meta.current_page,
            lastMinuteCancellationTotalPages: meta.total_pages,
            lastMinuteCancellationLoading: false,
            lastMinuteCancellationError: false,
          });
        },
        (error) => {
          if (currentFetchId !== lastMinuteCancellationFetchId) return;
          if (isDataLockError(error)) {
            set({
              lastMinuteCancellationDetails: LAST_MINUTE_CANCELLATIONS_STUB,
              lastMinuteCancellationTotalCount: LAST_MINUTE_CANCELLATIONS_STUB.length,
              lastMinuteCancellationPage: 1,
              lastMinuteCancellationTotalPages: 1,
              lastMinuteCancellationLoading: false,
              lastMinuteCancellationError: false,
            });
            return;
          }
          set({
            lastMinuteCancellationDetails: null,
            lastMinuteCancellationLoading: false,
            lastMinuteCancellationError: true,
          });
        },
      );
    },

    fetchData: (dateRange, locationIds, positions, employmentTypes) => {
      if (isSelectionCleared(locationIds, positions)) {
        fetchId++;
        set({ ...INITIAL_STATE });
        return () => {
          fetchId++;
        };
      }
      const currentFetchId = ++fetchId;
      set({
        isLoading: true,
        error: null,
        totalAssignedShiftsError: false,
        totalCalloutShiftsError: false,
        fulfillmentRateError: false,
        cancelledShiftsError: false,
      });

      const query = buildFilterQuery(dateRange, locationIds, positions, employmentTypes);
      calloutRateByPositionBaseQuery = query;
      fulfillmentByPositionBaseQuery = query;

      const totalAssignedRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_TOTAL_ASSIGNED(),
        method: 'GET',
        query,
      });

      const totalCancelledRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_TOTAL_CANCELLED(),
        method: 'GET',
        query,
      });

      const totalCalloutRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_TOTAL_CALLOUT(),
        method: 'GET',
        query,
      });

      const fulfillmentPercentageRequest = createDashboardsRequest<TotalShiftsResponse>({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_PERCENTAGE(),
        method: 'GET',
        query,
      });

      const fulfillmentByTimeframeRequest = createDashboardsRequest<
        TimeframeFulfillmentResponseItem[]
      >({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_BY_TIMEFRAME(),
        method: 'GET',
        query,
      });

      const calloutRateByPositionRequest = fetchDashboardPage<
        PositionCalloutRateResponse,
        PositionCalloutRateResponseItem
      >({
        url: DASHBOARDS_SHIFTS_CALLOUT_RATE_BY_POSITION(),
        query,
        page: 1,
        selectItems: (body) => body.callout_rate_by_position ?? [],
      });

      const fulfillmentByPositionRequest = fetchDashboardPage<
        FulfillmentByPositionResponse,
        FulfillmentByPositionResponseItem
      >({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_BY_POSITION(),
        query,
        page: 1,
        selectItems: (body) => body.fulfillment_by_position ?? [],
      });

      const peakCalloutHoursRequest = createDashboardsRequest<PeakCalloutHoursResponseItem[]>({
        url: DASHBOARDS_SHIFTS_PEAK_CALLOUT_HOURS(),
        method: 'GET',
        query,
      });

      const resolutionSpeedRequest = createDashboardsRequest<CalloutResolutionPerTimeframeResponse>(
        {
          url: DASHBOARDS_SHIFTS_CALLOUT_RESOLUTION_PER_TIMEFRAME(),
          method: 'GET',
          query,
        },
      );

      Promise.allSettled([
        totalAssignedRequest,
        totalCancelledRequest,
        totalCalloutRequest,
        fulfillmentPercentageRequest,
        fulfillmentByTimeframeRequest,
        calloutRateByPositionRequest,
        fulfillmentByPositionRequest,
        peakCalloutHoursRequest,
        resolutionSpeedRequest,
      ]).then((results) => {
        const isDataLocked = hasDataLockRejection(results);
        if (currentFetchId !== fetchId) return;
        if (isDataLocked) {
          set({ ...INITIAL_STATE, ...FULFILLMENT_DATA_LOCKED_STUB, isLoading: false });
          return;
        }
        const [
          totalAssignedResult,
          totalCancelledResult,
          totalCalloutResult,
          fulfillmentPercentageResult,
          fulfillmentByTimeframeResult,
          calloutRateByPositionResult,
          fulfillmentByPositionResult,
          peakCalloutHoursResult,
          resolutionSpeedResult,
        ] = results;

        const totalAssignedData = unwrapDashboardRoot<TotalShiftsResponse>(
          totalAssignedResult.status === 'fulfilled' ? totalAssignedResult.value.data : null,
          'total_assigned_shifts',
        );
        const totalCancelledData = unwrapDashboardRoot<TotalShiftsResponse>(
          totalCancelledResult.status === 'fulfilled' ? totalCancelledResult.value.data : null,
          'total_cancelled_shifts',
        );
        const totalCalloutData = unwrapDashboardRoot<TotalShiftsResponse>(
          totalCalloutResult.status === 'fulfilled' ? totalCalloutResult.value.data : null,
          'total_callout_shifts',
        );
        const fulfillmentPercentageData = unwrapDashboardRoot<TotalShiftsResponse>(
          fulfillmentPercentageResult.status === 'fulfilled'
            ? fulfillmentPercentageResult.value.data
            : null,
          'fulfillment_percentage',
        );
        const fulfillmentByTimeframeData = unwrapDashboardRoot<TimeframeFulfillmentResponseItem[]>(
          fulfillmentByTimeframeResult.status === 'fulfilled'
            ? fulfillmentByTimeframeResult.value.data
            : null,
          'fulfillment_by_timeframe',
        );
        const calloutRateByPositionData =
          calloutRateByPositionResult.status === 'fulfilled'
            ? calloutRateByPositionResult.value
            : null;
        const fulfillmentByPositionData =
          fulfillmentByPositionResult.status === 'fulfilled'
            ? fulfillmentByPositionResult.value
            : null;
        const peakCalloutHoursData =
          peakCalloutHoursResult.status === 'fulfilled' ? peakCalloutHoursResult.value.data : null;
        const resolutionSpeedData =
          resolutionSpeedResult.status === 'fulfilled' ? resolutionSpeedResult.value.data : null;

        set((state) => ({
          ...state,
          totalAssignedShifts: toNumber(totalAssignedData?.current_period_total),
          totalAssignedShiftsPeriodDays: totalAssignedData?.period_days ?? null,
          totalAssignedShiftsChangePrct:
            totalAssignedData?.change_prct == null ? null : toNumber(totalAssignedData.change_prct),
          totalAssignedShiftsError: totalAssignedResult.status === 'rejected',
          totalCalloutShifts: toNumber(totalCalloutData?.current_period_total),
          totalCalloutShiftsPeriodDays: totalCalloutData?.period_days ?? null,
          totalCalloutShiftsChangePrct:
            totalCalloutData?.change_prct == null ? null : toNumber(totalCalloutData.change_prct),
          totalCalloutShiftsError: totalCalloutResult.status === 'rejected',
          fulfillmentRate: toNumber(fulfillmentPercentageData?.current_period_total),
          fulfillmentRatePeriodDays: fulfillmentPercentageData?.period_days ?? null,
          fulfillmentRateChangePrct:
            fulfillmentPercentageData?.change_prct == null
              ? null
              : toNumber(fulfillmentPercentageData.change_prct),
          fulfillmentRateError: fulfillmentPercentageResult.status === 'rejected',
          cancelledShifts: toNumber(totalCancelledData?.current_period_total),
          cancelledShiftsPeriodDays: totalCancelledData?.period_days ?? null,
          cancelledShiftsChangePrct:
            totalCancelledData?.change_prct == null
              ? null
              : toNumber(totalCancelledData.change_prct),
          cancelledShiftsError: totalCancelledResult.status === 'rejected',
          calloutRateByPosition: calloutRateByPositionData
            ? mapCalloutRateByPosition(calloutRateByPositionData.items)
            : null,
          calloutRateByPositionPage: calloutRateByPositionData?.meta?.current_page ?? 1,
          calloutRateByPositionTotalPages: calloutRateByPositionData?.meta?.total_pages ?? 1,
          calloutRateByPositionLoadingMore: false,
          fulfillmentByTimeframe: fulfillmentByTimeframeData
            ? mapFulfillmentByTimeframe(fulfillmentByTimeframeData)
            : null,
          fulfillmentByPosition: fulfillmentByPositionData
            ? mapFulfillmentByPosition(fulfillmentByPositionData.items)
            : null,
          fulfillmentByPositionPage: fulfillmentByPositionData?.meta?.current_page ?? 1,
          fulfillmentByPositionTotalPages: fulfillmentByPositionData?.meta?.total_pages ?? 1,
          fulfillmentByPositionLoadingMore: false,
          peakCalloutHours: peakCalloutHoursData ? mapPeakCalloutHours(peakCalloutHoursData) : null,
          resolutionSpeed: resolutionSpeedData ? mapResolutionSpeed(resolutionSpeedData) : null,
          isLoading: false,
          error: null,
        }));
      });

      get().fetchUnfulfilledShiftDetails(1, dateRange, locationIds, positions, employmentTypes);
      get().fetchLastMinuteCancellations(1, dateRange, locationIds, positions, employmentTypes);

      return () => {
        fetchId++;
        unfulfilledDetailFetchId++;
        lastMinuteCancellationFetchId++;
      };
    },
    loadMoreCalloutRateByPosition: () => {
      const {
        calloutRateByPositionLoadingMore,
        calloutRateByPositionPage,
        calloutRateByPositionTotalPages,
      } = get();
      if (
        calloutRateByPositionLoadingMore ||
        calloutRateByPositionPage >= calloutRateByPositionTotalPages ||
        !calloutRateByPositionBaseQuery
      ) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = calloutRateByPositionPage + 1;
      set({ calloutRateByPositionLoadingMore: true });

      fetchDashboardPage<PositionCalloutRateResponse, PositionCalloutRateResponseItem>({
        url: DASHBOARDS_SHIFTS_CALLOUT_RATE_BY_POSITION(),
        query: calloutRateByPositionBaseQuery,
        page: nextPage,
        selectItems: (body) => body.callout_rate_by_position ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            calloutRateByPosition: [
              ...(state.calloutRateByPosition ?? []),
              ...mapCalloutRateByPosition(items),
            ],
            calloutRateByPositionPage: meta?.current_page ?? nextPage,
            calloutRateByPositionTotalPages:
              meta?.total_pages ?? state.calloutRateByPositionTotalPages,
            calloutRateByPositionLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ calloutRateByPositionLoadingMore: false });
        });
    },
    loadMoreFulfillmentByPosition: () => {
      const {
        fulfillmentByPositionLoadingMore,
        fulfillmentByPositionPage,
        fulfillmentByPositionTotalPages,
      } = get();
      if (
        fulfillmentByPositionLoadingMore ||
        fulfillmentByPositionPage >= fulfillmentByPositionTotalPages ||
        !fulfillmentByPositionBaseQuery
      ) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = fulfillmentByPositionPage + 1;
      set({ fulfillmentByPositionLoadingMore: true });

      fetchDashboardPage<FulfillmentByPositionResponse, FulfillmentByPositionResponseItem>({
        url: DASHBOARDS_SHIFTS_FULFILLMENT_BY_POSITION(),
        query: fulfillmentByPositionBaseQuery,
        page: nextPage,
        selectItems: (body) => body.fulfillment_by_position ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            fulfillmentByPosition: [
              ...(state.fulfillmentByPosition ?? []),
              ...mapFulfillmentByPosition(items),
            ],
            fulfillmentByPositionPage: meta?.current_page ?? nextPage,
            fulfillmentByPositionTotalPages:
              meta?.total_pages ?? state.fulfillmentByPositionTotalPages,
            fulfillmentByPositionLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ fulfillmentByPositionLoadingMore: false });
        });
    },
    setData: (data) => set((state) => ({ ...state, ...data })),
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    reset: () => {
      fetchId = 0;
      unfulfilledDetailFetchId = 0;
      lastMinuteCancellationFetchId = 0;
      calloutRateByPositionBaseQuery = null;
      fulfillmentByPositionBaseQuery = null;
      set(INITIAL_STATE);
    },
  })),
);

export const useFulfillmentDashboardStore = fulfillmentDashboardStore;
